import { BaseComponent } from '../../base-component';
import { Topics as SponsoredContentAlternativeTopics } from '../../sponsored-content-alternative/client/sponsored-content-alternative.component.types';
import { Fold, SdaEvent, Topic as SdaTopics } from '../../sda/types';
import { EventCallback } from '@otto-ec/global-resources/event-q-bus';

export class SponsoredContentAndPromotionsComponent extends BaseComponent {
    public static readonly rootClass = 'js_pdp_sponsored-content-and-promotions';
    public readonly rootSelector = `.${SponsoredContentAndPromotionsComponent.rootClass}`;
    private static readonly skeletonClass = 'js_pdp_sponsored-content-and-promotions__skeleton';
    private static readonly kuvoClass = 'js_pdp_sponsored-content-and-promotions__kuvo';
    public static readonly sdaClass = 'js_pdp_sponsored-content-and-promotions__sda';
    private static readonly sponsoredContentAlternativeClass = 'js_pdp_sponsored-content-and-promotions__alternative';
    private static readonly FT9_BENEFIT_INIT_EVENT = 'ft9.benefit.init';

    private static elementClasses = [
        SponsoredContentAndPromotionsComponent.kuvoClass,
        SponsoredContentAndPromotionsComponent.sdaClass,
        SponsoredContentAndPromotionsComponent.sponsoredContentAlternativeClass,
        SponsoredContentAndPromotionsComponent.skeletonClass,
    ];

    private readonly callbacks: Map<`${string}.${string}.${string}`, EventCallback | null> = new Map();

    public async init(): Promise<unknown> {
        return;
    }

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }
        this.cleanUpEventQBusCallbacks();

        const kuvo = this.getKuvo();
        this.initializeKuvo();
        if (this.isKuvoThere(kuvo)) {
            return;
        }

        this.hideKuvo();

        if (!this.isThereSdaJs()) {
            this.showSponsoredContentAlternative();
        } else {
            o_global.eventQBus.emitRetain('as.odin.sda.initialize', {
                slotIdentifier: this.getRootElement()?.querySelector<HTMLElement>(`.${SponsoredContentAndPromotionsComponent.sdaClass}`)
                    ?.dataset.sdaSlotId,
                isVariationChange: !!o_product?.state?.isVariationChanged,
                eventMergeId: o_product.tracking.eventMergeId,
            });
        }
        this.callbacks.set(SdaTopics.Initialized, o_global.eventQBus.on(SdaTopics.Initialized, this.handleSdaInitialized.bind(this)));
    }

    protected override show() {
        this.getRootElement().classList.remove('oc-ft5-visually-hidden');
    }

    protected override hide() {
        this.getRootElement().classList.add('oc-ft5-visually-hidden');
    }

    private hideKuvo() {
        this.hideElementByClass(SponsoredContentAndPromotionsComponent.kuvoClass);
        this.showElementByClass(SponsoredContentAndPromotionsComponent.skeletonClass);
    }

    private handleSdaInitialized(event: SdaEvent) {
        if (event.slotIdentifier.includes(Fold.Above)) {
            if (!event.rendered) {
                this.showSponsoredContentAlternative();
            }
            if (event.rendered) {
                this.showSda();
            }
        }
    }

    private cleanUpEventQBusCallbacks() {
        this.callbacks.forEach((callback: EventCallback | null, topic: `${string}.${string}.${string}`, _) => {
            if (callback) {
                o_global.eventQBus.off(topic, callback);
            }
        });
    }

    private showSponsoredContentAlternative() {
        o_global.eventQBus.emitRetain(SponsoredContentAlternativeTopics.SPONSORED_CONTENT_ALTERNATIVE_REVEAL);
        this.callbacks.set(
            SponsoredContentAlternativeTopics.SPONSORED_CONTENT_ALTERNATIVE_READY,
            o_global.eventQBus.on(
                SponsoredContentAlternativeTopics.SPONSORED_CONTENT_ALTERNATIVE_READY,
                this.handleSponsoredContentAlternativeReady.bind(this)
            )
        );
        this.callbacks.set(
            SponsoredContentAlternativeTopics.SPONSORED_CONTENT_ALTERNATIVE_FAILED,
            o_global.eventQBus.on(
                SponsoredContentAlternativeTopics.SPONSORED_CONTENT_ALTERNATIVE_FAILED,
                this.handleSponsoredContentAlternativeFailed.bind(this)
            )
        );
    }

    private handleSponsoredContentAlternativeReady() {
        this.showElementByClass(SponsoredContentAndPromotionsComponent.sponsoredContentAlternativeClass);
        this.cleanUpEventQBusCallbacks();
    }

    private handleSponsoredContentAlternativeFailed() {
        this.hideElementByClass(SponsoredContentAndPromotionsComponent.skeletonClass);
        this.cleanUpEventQBusCallbacks();
    }

    private isKuvoThere(kuvo: HTMLElement | null) {
        return kuvo && kuvo?.children?.length > 0 && !kuvo.classList.contains('oc-ft5-visually-hidden');
    }

    private initializeKuvo() {
        o_global.eventQBus.emit(SponsoredContentAndPromotionsComponent.FT9_BENEFIT_INIT_EVENT);
    }

    private getKuvo() {
        return this.getRootElement().querySelector<HTMLElement>(`.${SponsoredContentAndPromotionsComponent.kuvoClass}`);
    }

    private isThereSdaJs() {
        return !!this.getRootElement()?.querySelector(`.${SponsoredContentAndPromotionsComponent.sdaClass}`)?.textContent?.trim();
    }

    private showSda() {
        this.showElementByClass(SponsoredContentAndPromotionsComponent.sdaClass);
    }

    private hideElementByClass(elementClass: string): void {
        this.getRootElement()?.querySelector<HTMLElement>(`.${elementClass}`)?.classList.add('oc-ft5-visually-hidden');
    }

    private showElementByClass(elementClass: string): void {
        const element = this.getRootElement()?.querySelector<HTMLElement>(`.${elementClass}`);
        element && this.showElement(element);
    }

    private showElement(element: HTMLElement): void {
        SponsoredContentAndPromotionsComponent.elementClasses.forEach(this.hideElementByClass.bind(this));
        element.classList.remove('oc-ft5-visually-hidden');
    }
}
